import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarPanel } from '../components/admin/CalendarPanel';
import { DateDetailPanel } from '../components/admin/DateDetailPanel';
import { UpcomingEvents } from '../components/admin/UpcomingEvents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BOOKED_STATUSES = ['confirmed', 'reserved'];

const toIsoDate = (d) => d.toISOString().split('T')[0];

const AdminCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDates, setCalendarDates] = useState([]);
  const [bookedDates, setBookedDates] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [showBlockForm, setShowBlockForm] = useState(false);

  const fetchCalendarDates = useCallback(async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;

      const response = await axios.get(
        `${API}/admin/calendar?year=${year}&month=${month}`,
        { withCredentials: true }
      );

      setCalendarDates(response.data.dates);
      setBookedDates(
        response.data.dates
          .filter(d => BOOKED_STATUSES.includes(d.status))
          .map(d => new Date(d.date))
      );
      setBlockedDates(
        response.data.dates
          .filter(d => d.status === 'blocked')
          .map(d => new Date(d.date))
      );
    } catch (error) {
      console.error('Error fetching calendar:', error);
      toast.error('Failed to load calendar');
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchCalendarDates();
  }, [fetchCalendarDates]);

  const handleBlockDate = async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const dateStr = toIsoDate(selectedDate);
      await axios.post(
        `${API}/admin/block-date`,
        { date: dateStr, reason: blockReason },
        { withCredentials: true }
      );

      toast.success(`Date ${dateStr} blocked successfully`);
      setShowBlockForm(false);
      setBlockReason('');
      setSelectedDate(null);
      fetchCalendarDates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block date');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblockDate = async (dateStr) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/unblock-date/${dateStr}`, { withCredentials: true });
      toast.success(`Date ${dateStr} unblocked successfully`);
      fetchCalendarDates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unblock date');
    } finally {
      setLoading(false);
    }
  };

  const getDateInfo = useCallback(
    (date) => {
      const dateStr = toIsoDate(date);
      return calendarDates.find(d => d.date === dateStr);
    },
    [calendarDates]
  );

  const handleDateSelect = (date) => {
    if (!date) return;
    const dateInfo = getDateInfo(date);

    if (dateInfo && dateInfo.status === 'blocked') {
      if (window.confirm(`Unblock ${toIsoDate(date)}?`)) {
        handleUnblockDate(toIsoDate(date));
      }
      return;
    }
    if (!dateInfo) {
      setSelectedDate(date);
      setShowBlockForm(true);
      return;
    }
    setSelectedDate(date);
    setShowBlockForm(false);
  };

  const selectedDateInfo = useMemo(
    () => (selectedDate ? getDateInfo(selectedDate) : null),
    [selectedDate, getDateInfo]
  );

  return (
    <div className="py-12 bg-[#F5F1E8]" data-testid="admin-calendar-page">
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-4xl font-serif text-[#8B7355] mb-8 italic">Booking Calendar</h2>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <CalendarPanel
              selectedDate={selectedDate}
              currentMonth={currentMonth}
              setCurrentMonth={setCurrentMonth}
              onDateSelect={handleDateSelect}
              bookedDates={bookedDates}
              blockedDates={blockedDates}
            />
          </div>

          <div className="lg:col-span-1">
            <DateDetailPanel
              selectedDate={selectedDate}
              selectedDateInfo={selectedDateInfo}
              showBlockForm={showBlockForm}
              blockReason={blockReason}
              setBlockReason={setBlockReason}
              loading={loading}
              onBlock={handleBlockDate}
              onUnblock={handleUnblockDate}
              onClose={() => { setShowBlockForm(false); setSelectedDate(null); }}
            />
          </div>
        </div>

        <UpcomingEvents calendarDates={calendarDates} />
      </div>
    </div>
  );
};

export default AdminCalendar;
